import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import Input from "@/components/shared/Input";
import {
  useCreateMachineMutation,
  useGetMachineByIdQuery,
  useUpdateMachineMutation,
} from "@/features/models/machinesApi";
import { createMachineSchema, updateMachineSchema } from "@/schemas/machine";
import LoadingDots from "@/components/shared/LoadingDots";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { Plus, X } from "lucide-react";
import ApiKeyModal from "../ApiKeyModal";
import { Wilayas } from "@/lib/wilayas";
import { useSelector } from "react-redux";
import { selectUser } from "@/features/auth/authSlice";

const MachineForm = ({ edit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [createdMachine, setCreatedMachine] = useState(null);
  const currentUser = useSelector(selectUser);
  if (currentUser.role !== "admin") {
    navigate("/dashboard");
  }

  const { data: machine, isLoading: isMachineLoading } = useGetMachineByIdQuery(
    id,
    {
      skip: !edit || !id,
    }
  );

  const [addMachine, { isLoading: isAdding }] = useCreateMachineMutation();
  const [updateMachine, { isLoading: isUpdating }] = useUpdateMachineMutation();

  const form = useForm({
    resolver: zodResolver(edit ? updateMachineSchema : createMachineSchema),
    defaultValues: {
      id: "",
      name: "",
      size: "86",
      wilaya: "",
      isActive: true,
      ip_whitelist: [],
    },
    mode: "onBlur",
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "ip_whitelist",
  });

  useEffect(() => {
    if (edit && machine) {
      const formData = {
        id: machine.id || "",
        name: machine.name || "",
        size: String(machine.size || "86"),
        wilaya: String(machine.wilaya || ""),
        isActive: machine.isActive !== undefined ? machine.isActive : true,
        ip_whitelist:
          machine.ip_whitelist && machine.ip_whitelist.length > 0
            ? machine.ip_whitelist
            : [],
      };

      form.reset(formData);

      setTimeout(() => {
        form.setValue("size", formData.size, { shouldDirty: false });
        form.setValue("wilaya", formData.wilaya, { shouldDirty: false });
      }, 50);
    }
  }, [edit, machine]);
  const onSubmit = async (data) => {
    try {
      // Clean up the data
      const payload = { ...data };

      // Filter out empty IP addresses and trim whitespace
      payload.ip_whitelist = data.ip_whitelist
        .filter((ip) => ip && ip.trim() !== "")
        .map((ip) => ip.trim());

      // If no valid IPs, set to empty array (schema allows empty array)
      if (!payload.ip_whitelist || payload.ip_whitelist.length === 0) {
        payload.ip_whitelist = [];
      }

      if (edit) {
        await updateMachine({ id, ...payload }).unwrap();
        toast.success("Machine updated successfully");
        navigate("/dashboard/machines");
      } else {
        const result = await addMachine(payload).unwrap();

        // Show the API key modal with the created machine data
        setCreatedMachine(result);
        setShowApiKeyModal(true);

        toast.success("Machine created successfully");
      }
    } catch (error) {
      console.error("Error saving machine:", error);
      toast.error(error?.data?.error || "Failed to save machine");
    }
  };

  const handleApiKeyModalClose = () => {
    setShowApiKeyModal(false);
    setCreatedMachine(null);
    navigate("/dashboard/machines");
  };

  const addIpField = () => {
    append("");
  };

  const removeIpField = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  if (edit && isMachineLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingDots />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardHeader className="flex items-center justify-between">
            <div>
              <CardTitle>
                {edit ? "Edit Machine" : "Create New Machine"}
              </CardTitle>
              <CardDescription>
                {edit
                  ? "Update machine information"
                  : "Fill in the details to register a new machine"}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Form {...form} key={edit && machine ? machine.id : "create"}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Machine name"
                      {...form.register("name")}
                      error={form.formState.errors.name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>

                <FormItem>
                  <FormLabel>Size</FormLabel>
                  <FormControl>
                    <Controller
                      name="size"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <div>
                          <Select
                            value={field.value || ""}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select size" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="86">86</SelectItem>
                              <SelectItem value="65">65</SelectItem>
                            </SelectContent>
                          </Select>
                          {fieldState.error && (
                            <p className="text-red-500 text-sm mt-1">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </FormControl>
                </FormItem>

                <FormItem>
                  <FormLabel>Wilaya</FormLabel>
                  <FormControl>
                    <Controller
                      name="wilaya"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <div>
                          <Select
                            value={field.value || ""}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select Wilaya" />
                            </SelectTrigger>
                            <SelectContent>
                              {Wilayas.map((wilaya) => (
                                <SelectItem key={wilaya} value={wilaya}>
                                  {wilaya}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {fieldState.error && (
                            <p className="text-red-500 text-sm mt-1">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </FormControl>
                </FormItem>

                <FormItem>
                  <div className="flex items-center justify-between">
                    <div>
                      <FormLabel>Active Status</FormLabel>
                      <div className="text-sm text-gray-600">
                        Enable or disable this machine
                      </div>
                    </div>
                    <FormControl>
                      <Controller
                        name="isActive"
                        control={form.control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>

                {edit && machine && (
                  <FormItem>
                    <FormLabel>Machine Licence Code</FormLabel>
                    <FormControl>
                      <Input value={machine.code.fullCode} disabled />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}

                <div>
                  <FormLabel>IP Whitelist (Optional)</FormLabel>
                  <div className="text-sm text-gray-600 mb-2">
                    Leave empty to skip the IP check for this machine.
                  </div>
                  <div className="space-y-2">
                    {fields.map((field, index) => (
                      <div key={field.id} className="flex items-center gap-2">
                        <FormControl>
                          <Input
                            placeholder="192.168.1.100"
                            {...form.register(`ip_whitelist.${index}`)}
                            error={form.formState.errors.ip_whitelist?.[index]}
                          />
                        </FormControl>
                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeIpField(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addIpField}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add IP Address
                    </Button>
                  </div>
                  {typeof form.formState.errors.ip_whitelist?.message ===
                    "string" && (
                    <p className="pl-2 text-red-500 text-sm mt-1">
                      {form.formState.errors.ip_whitelist.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={isAdding || isUpdating}
                    className="flex items-center gap-2"
                  >
                    {(isAdding || isUpdating) && <LoadingDots />}
                    {edit ? "Update Machine" : "Create Machine"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/dashboard/machines")}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      {/* API Key Modal */}
      <ApiKeyModal
        isOpen={showApiKeyModal}
        onClose={handleApiKeyModalClose}
        apiKey={createdMachine?.code?.fullCode}
        machineName={createdMachine?.name}
        codePrefix={createdMachine?.code?.prefix}
      />
    </>
  );
};

export default MachineForm;
